import { useRef, useEffect, useState } from "react";

import { TooltipBase } from "@frontend/common";
import { splitMultipleStringList } from "@frontend/common/src/business/DatabaseTable/utils/splitMultipleStringList";

import { RegulatoryListData } from "@/components/business/RegulatoryContainer/Regulatory.types";
import LabelingPressTitle from "@/components/shared/ui/LabelingPressTitle/LabelingPressTitle";
import { ProcessStatusCode } from "@/types/code.types";
import { regulatoryListApis } from "@api/regulatory/regulatoryList/regulatory.service";
import { DRAFT_STATUS_CODE } from "@constants/draftStatusCode.constants";
import { useGetCurrentEntryData } from "@hooks/useGetCurrentEntryData";
import { toastFail } from "@utils/toast";

import { useMoveRegulatoryDetailPage } from "./hooks/useMoveRegulatoryDetailPage";
import * as S from "./RegulatoryTableShellForRegulatoryTitle.style";

interface Props {
  regulatoryData: RegulatoryListData;
  openCheckStatusModal: () => void;
  openAccessDeniedModal: () => void;
  openConcurrentAccessDeniedModal: () => void;
  handleTargetRegulatoryId: () => void;
  handleCurrentStatus: (status: ProcessStatusCode | "") => void;
}

const RegulatoryTableShellForRegulatoryTitle = ({
  regulatoryData,
  openCheckStatusModal,
  openAccessDeniedModal,
  openConcurrentAccessDeniedModal,
  handleTargetRegulatoryId,
  handleCurrentStatus,
}: Props) => {
  const [isTruncated, setIsTruncated] = useState(false);
  const titleRef = useRef<HTMLDivElement>(null);
  const draftArticleId = regulatoryData?.draftArticleId?.toString();
  const getEntryData = useGetCurrentEntryData(draftArticleId);

  useEffect(() => {
    const checkTruncation = () => {
      if (titleRef.current) {
        const { scrollHeight, clientHeight } = titleRef.current;
        setIsTruncated(scrollHeight > clientHeight + 1);
      }
    };

    setTimeout(checkTruncation, 0);
    window.addEventListener("resize", checkTruncation);

    return () => {
      window.removeEventListener("resize", checkTruncation);
    };
  }, [regulatoryData?.labelName]);

  const { moveRegulatoryDetailPage } = useMoveRegulatoryDetailPage({
    draftArticleId,
    labelId: regulatoryData?.labelId,
    ftEditorName: regulatoryData?.ftEditor2Name,
    onAccessDenied: openAccessDeniedModal,
    onConcurrentAccessDenied: openConcurrentAccessDeniedModal,
  });

  const saveScrollPositionToSessionStorage = () => {
    sessionStorage.setItem("scrollPosition", window.scrollY.toString());
  };

  const checkCurrentStatus = async () => {
    try {
      // TODO: BE regulatory status API 확정 시 regulatoryListApis.checkPressStatus 교체
      const { data: currentStatus } = await regulatoryListApis.checkPressStatus(
        draftArticleId,
      );

      // currentStatus가 ai done, draftArticleStatusHighest가 not for ai인 경우 Alert이 표출되지 않도록 설정
      if (
        currentStatus === DRAFT_STATUS_CODE.aiEntryDone &&
        regulatoryData.draftArticleStatusHighest ===
          DRAFT_STATUS_CODE.notForAiEntry
      ) {
        saveScrollPositionToSessionStorage();
        await moveRegulatoryDetailPage();

        return;
      }
      if (currentStatus !== regulatoryData.draftArticleStatusHighest) {
        handleCurrentStatus(currentStatus as ProcessStatusCode | "");
        handleTargetRegulatoryId();
        openCheckStatusModal();
      } else {
        saveScrollPositionToSessionStorage();
        await moveRegulatoryDetailPage();
      }
    } catch (error) {
      toastFail("Could not check the current status of the article.");
    }
  };

  const title = regulatoryData?.labelName || "-";
  const titleList = splitMultipleStringList(title).filter(Boolean);
  const isMultipleTitle = title.includes("__");
  const visibleTitleList = titleList.slice(0, 2);
  const remainingTitleCount = titleList.length > 2 ? titleList.length - 2 : 0;
  const hasTitleTooltip =
    isTruncated || (isMultipleTitle && remainingTitleCount > 0);

  const clickToMovePressDetailPage = async () => {
    getEntryData();
    await checkCurrentStatus();
  };

  const renderTitleBulletList = (items: string[]) => {
    return (
      <S.RegulatoryTitleBulletList>
        {items.map((item) => (
          <S.RegulatoryTitleBulletItem key={`${item}-${title}`}>
            <S.RegulatoryTitleBulletItemText>
              <LabelingPressTitle title={item} />
            </S.RegulatoryTitleBulletItemText>
          </S.RegulatoryTitleBulletItem>
        ))}
      </S.RegulatoryTitleBulletList>
    );
  };

  const renderTitle = () => {
    if (!isMultipleTitle) {
      return <LabelingPressTitle title={title} />;
    }

    if (titleList.length === 0) {
      return <LabelingPressTitle title="-" />;
    }

    return (
      <>
        {renderTitleBulletList(visibleTitleList)}

        {remainingTitleCount > 0 && (
          <S.RegulatoryTitleNumber>
            +{remainingTitleCount}
          </S.RegulatoryTitleNumber>
        )}
      </>
    );
  };

  const renderTooltipTitle = () => {
    if (!isMultipleTitle || titleList.length === 0) {
      return <LabelingPressTitle title={title} />;
    }

    return renderTitleBulletList(titleList);
  };

  return (
    <S.PressTitleTableShell onClick={clickToMovePressDetailPage}>
      <S.TitleWrapper>
        <div ref={titleRef}>{renderTitle()}</div>
      </S.TitleWrapper>
      {hasTitleTooltip && (
        <S.TooltipWrapper className="tooltip-container">
          <TooltipBase className="title-tooltip">
            {renderTooltipTitle()}
          </TooltipBase>
        </S.TooltipWrapper>
      )}
    </S.PressTitleTableShell>
  );
};

export default RegulatoryTableShellForRegulatoryTitle;
